import { DEFAULT_CURRENCY_RATES } from './shippingAndPayment';

export interface StateTaxInfo {
  state: string;
  name: string;
  standardIcmsPercent: number;
}

export const BRAZIL_STATE_ICMS: Record<string, StateTaxInfo> = {
  AC: { state: 'AC', name: 'Acre', standardIcmsPercent: 19 },
  AL: { state: 'AL', name: 'Alagoas', standardIcmsPercent: 19 },
  AM: { state: 'AM', name: 'Amazonas', standardIcmsPercent: 20 },
  AP: { state: 'AP', name: 'Amapá', standardIcmsPercent: 18 },
  BA: { state: 'BA', name: 'Bahia', standardIcmsPercent: 20.5 },
  CE: { state: 'CE', name: 'Ceará', standardIcmsPercent: 20 },
  DF: { state: 'DF', name: 'Distrito Federal', standardIcmsPercent: 20 },
  ES: { state: 'ES', name: 'Espírito Santo', standardIcmsPercent: 17 },
  GO: { state: 'GO', name: 'Goiás', standardIcmsPercent: 19 },
  MA: { state: 'MA', name: 'Maranhão', standardIcmsPercent: 22 },
  MG: { state: 'MG', name: 'Minas Gerais', standardIcmsPercent: 18 },
  MS: { state: 'MS', name: 'Mato Grosso do Sul', standardIcmsPercent: 17 },
  MT: { state: 'MT', name: 'Mato Grosso', standardIcmsPercent: 17 },
  PA: { state: 'PA', name: 'Pará', standardIcmsPercent: 19 },
  PB: { state: 'PB', name: 'Paraíba', standardIcmsPercent: 20 },
  PE: { state: 'PE', name: 'Pernambuco', standardIcmsPercent: 20.5 },
  PI: { state: 'PI', name: 'Piauí', standardIcmsPercent: 21 },
  PR: { state: 'PR', name: 'Paraná', standardIcmsPercent: 19.5 },
  RJ: { state: 'RJ', name: 'Rio de Janeiro', standardIcmsPercent: 20 },
  RN: { state: 'RN', name: 'Rio Grande do Norte', standardIcmsPercent: 18 },
  RO: { state: 'RO', name: 'Rondônia', standardIcmsPercent: 19.5 },
  RR: { state: 'RR', name: 'Roraima', standardIcmsPercent: 20 },
  RS: { state: 'RS', name: 'Rio Grande do Sul', standardIcmsPercent: 17 },
  SC: { state: 'SC', name: 'Santa Catarina', standardIcmsPercent: 17 },
  SE: { state: 'SE', name: 'Sergipe', standardIcmsPercent: 19 },
  SP: { state: 'SP', name: 'São Paulo', standardIcmsPercent: 18 },
  TO: { state: 'TO', name: 'Tocantins', standardIcmsPercent: 20 },
};

export type TaxRegime = 'remessa_conforme' | 'traditional';

export interface ImportTaxInput {
  itemsValue: number;
  currency: 'JPY' | 'USD' | 'BRL';
  shippingCostUsd: number;
  insuranceUsd?: number;
  stateCode?: string;
  customIcmsPercent?: number;
  regime: TaxRegime;
  includePostalFee?: boolean;
  usdToBrlRate?: number;
  jpyToUsdRate?: number;
  jpyToBrlRate?: number;
}

export interface ImportTaxBreakdown {
  // Inputs normalized
  itemsValueUsd: number;
  itemsValueBrl: number;
  itemsValueJpy: number;
  shippingCostUsd: number;
  shippingCostBrl: number;
  shippingCostJpy: number;
  insuranceUsd: number;
  insuranceBrl: number;

  // Customs base value (CIF)
  cifUsd: number;
  cifBrl: number;

  // Tier info
  isUnder50Usd: boolean;
  regime: TaxRegime;

  // Federal Import Tax (II)
  iiRatePercent: number;
  iiDeductionUsd: number;
  iiDeductionBrl: number;
  iiValueUsd: number;
  iiValueBrl: number;

  // State ICMS (calculated "por dentro")
  icmsRatePercent: number;
  icmsBaseBrl: number;
  icmsValueBrl: number;
  icmsValueUsd: number;

  // Postal dispatch fee (R$ 15,00)
  postalFeeBrl: number;
  postalFeeUsd: number;

  // Totals
  totalTaxesBrl: number;
  totalTaxesUsd: number;
  totalLandedCostBrl: number;
  totalLandedCostUsd: number;
  effectiveTaxRateOnCifPercent: number;
  effectiveTaxRateOnItemsPercent: number;
}

/**
 * Calculates complete Brazilian import tax breakdown (II + ICMS por dentro + Despacho Postal)
 * In accordance with Federal Law 14.902/2024 (Lei do Mover / Taxação de compras internacionais)
 * and CONFAZ standard ICMS rules for international mail.
 */
export function calculateBrazilImportTaxes(params: ImportTaxInput): ImportTaxBreakdown {
  const usdToBrl = params.usdToBrlRate || DEFAULT_CURRENCY_RATES.usdToBrl;
  const jpyToUsd = params.jpyToUsdRate || DEFAULT_CURRENCY_RATES.jpyToUsd;
  const jpyToBrl = params.jpyToBrlRate || DEFAULT_CURRENCY_RATES.jpyToBrl;

  // Normalize items value
  let itemsValueUsd = 0;
  let itemsValueBrl = 0;
  let itemsValueJpy = 0;

  if (params.currency === 'USD') {
    itemsValueUsd = Math.max(0, params.itemsValue);
    itemsValueBrl = itemsValueUsd * usdToBrl;
    itemsValueJpy = Math.round(itemsValueUsd / jpyToUsd);
  } else if (params.currency === 'BRL') {
    itemsValueBrl = Math.max(0, params.itemsValue);
    itemsValueUsd = itemsValueBrl / usdToBrl;
    itemsValueJpy = Math.round(itemsValueBrl / jpyToBrl);
  } else {
    // JPY
    itemsValueJpy = Math.max(0, Math.round(params.itemsValue));
    itemsValueUsd = itemsValueJpy * jpyToUsd;
    itemsValueBrl = itemsValueJpy * jpyToBrl;
  }

  const shippingCostUsd = Math.max(0, params.shippingCostUsd);
  const shippingCostBrl = shippingCostUsd * usdToBrl;
  const shippingCostJpy = Math.round(shippingCostUsd / jpyToUsd);

  const insuranceUsd = Math.max(0, params.insuranceUsd || 0);
  const insuranceBrl = insuranceUsd * usdToBrl;

  // Total Customs CIF (Cost, Insurance & Freight)
  const cifUsd = itemsValueUsd + shippingCostUsd + insuranceUsd;
  const cifBrl = itemsValueBrl + shippingCostBrl + insuranceBrl;

  const isUnder50Usd = cifUsd <= 50.0;

  // Calculate Federal Import Tax (II)
  let iiRatePercent = 0;
  let iiDeductionUsd = 0;
  let iiValueUsd = 0;

  if (params.regime === 'remessa_conforme') {
    if (isUnder50Usd) {
      // 20% on CIF value up to $50 USD
      iiRatePercent = 20;
      iiDeductionUsd = 0;
      iiValueUsd = cifUsd * 0.20;
    } else {
      // 60% on CIF value above $50 USD with $20 fixed reduction (Lei 14.902/2024)
      iiRatePercent = 60;
      iiDeductionUsd = 20.00;
      iiValueUsd = Math.max(0, (cifUsd * 0.60) - iiDeductionUsd);
    }
  } else {
    // Traditional Postal Regime (RTS standard 60% linear without deduction)
    iiRatePercent = 60;
    iiDeductionUsd = 0;
    iiValueUsd = cifUsd * 0.60;
  }

  const iiDeductionBrl = iiDeductionUsd * usdToBrl;
  const iiValueBrl = iiValueUsd * usdToBrl;

  // ICMS Rate (17% unified CONFAZ standard or state specific if provided)
  const icmsRatePercent = params.customIcmsPercent !== undefined 
    ? params.customIcmsPercent 
    : (params.regime === 'remessa_conforme' ? 17 : (BRAZIL_STATE_ICMS[params.stateCode || 'SP']?.standardIcmsPercent || 17));

  const icmsRateDecimal = icmsRatePercent / 100;

  // ICMS Calculation "por dentro":
  // Base de Cálculo ICMS = (Valor Aduaneiro + II) / (1 - Alíquota ICMS)
  // Valor ICMS = Base de Cálculo * Alíquota ICMS
  const denominator = Math.max(0.01, 1 - icmsRateDecimal);
  const icmsBaseBrl = (cifBrl + iiValueBrl) / denominator;
  const icmsValueBrl = icmsBaseBrl * icmsRateDecimal;
  const icmsValueUsd = icmsValueBrl / usdToBrl;

  // Postal Dispatch Fee (R$ 15,00 Correios fee for traditional postal flow)
  const postalFeeBrl = (params.includePostalFee && params.regime === 'traditional') ? 15.00 : 0;
  const postalFeeUsd = postalFeeBrl / usdToBrl;

  // Total Taxes
  const totalTaxesBrl = iiValueBrl + icmsValueBrl + postalFeeBrl;
  const totalTaxesUsd = iiValueUsd + icmsValueUsd + postalFeeUsd;

  // Total Landed Cost (Products + Shipping + Insurance + Taxes)
  const totalLandedCostBrl = cifBrl + totalTaxesBrl;
  const totalLandedCostUsd = cifUsd + totalTaxesUsd;

  const effectiveTaxRateOnCifPercent = cifBrl > 0 ? (totalTaxesBrl / cifBrl) * 100 : 0;
  const effectiveTaxRateOnItemsPercent = itemsValueBrl > 0 ? (totalTaxesBrl / itemsValueBrl) * 100 : 0;

  return {
    itemsValueUsd: parseFloat(itemsValueUsd.toFixed(2)),
    itemsValueBrl: parseFloat(itemsValueBrl.toFixed(2)),
    itemsValueJpy,
    shippingCostUsd: parseFloat(shippingCostUsd.toFixed(2)),
    shippingCostBrl: parseFloat(shippingCostBrl.toFixed(2)),
    shippingCostJpy,
    insuranceUsd: parseFloat(insuranceUsd.toFixed(2)),
    insuranceBrl: parseFloat(insuranceBrl.toFixed(2)),

    cifUsd: parseFloat(cifUsd.toFixed(2)),
    cifBrl: parseFloat(cifBrl.toFixed(2)),

    isUnder50Usd,
    regime: params.regime,

    iiRatePercent,
    iiDeductionUsd: parseFloat(iiDeductionUsd.toFixed(2)),
    iiDeductionBrl: parseFloat(iiDeductionBrl.toFixed(2)),
    iiValueUsd: parseFloat(iiValueUsd.toFixed(2)),
    iiValueBrl: parseFloat(iiValueBrl.toFixed(2)),

    icmsRatePercent,
    icmsBaseBrl: parseFloat(icmsBaseBrl.toFixed(2)),
    icmsValueBrl: parseFloat(icmsValueBrl.toFixed(2)),
    icmsValueUsd: parseFloat(icmsValueUsd.toFixed(2)),

    postalFeeBrl,
    postalFeeUsd: parseFloat(postalFeeUsd.toFixed(2)),

    totalTaxesBrl: parseFloat(totalTaxesBrl.toFixed(2)),
    totalTaxesUsd: parseFloat(totalTaxesUsd.toFixed(2)),
    totalLandedCostBrl: parseFloat(totalLandedCostBrl.toFixed(2)),
    totalLandedCostUsd: parseFloat(totalLandedCostUsd.toFixed(2)),
    effectiveTaxRateOnCifPercent: parseFloat(effectiveTaxRateOnCifPercent.toFixed(1)),
    effectiveTaxRateOnItemsPercent: parseFloat(effectiveTaxRateOnItemsPercent.toFixed(1)),
  };
}
